import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'

export default function useBarometerSurveyCreatedList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError, getLocationsForDropDown, locations } = useCommon()
  const { updateItem, getItemsAggregate, getItems, sendEmail } = useLambda()
  const { getEmailBarometerSurveyTemplate } = useCommonTodo()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const barometerSurveysCreated = ref([])
  const collection = 'barometer_survey_created'
  // Table Headers
  const tableColumns = computed(() => ([
    { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
    { key: 'physical_location', label: i18n.t('message.tableHeader.physical_location'), sortable: true },
    { key: 'location', label: i18n.t('message.tableHeader.location'), sortable: true },
    // { key: 'link', label: i18n.t('message.tableHeader.link') },
    { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
  ]))

  const totalBarometerSurveysCreated = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)
  const showMailModal = ref(false)
  const workers = ref([])
  const mailData = ref({})

  const fetchBarometerSurveysCreated = async (searchValue = '') => {
    isLoading.value = true

    try {
      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'location',
            localField: 'location',
            foreignField: '_id',
            as: 'locationData'
          }
        },
        {
          $lookup: {
            from: 'physical_location',
            localField: 'physical_location',
            foreignField: '_id',
            as: 'physicalLocationData'
          }
        },
        {
          $addFields: {
            location: { $arrayElemAt: ['$locationData.location', 0] },
            physical_location: { $arrayElemAt: ['$physicalLocationData.name', 0] },
            locationId: {
              $toString: { $arrayElemAt: ['$locationData._id', 0] }
            },
            physicalLocationId: {
              $toString: { $arrayElemAt: ['$physicalLocationData._id', 0] }
            }
          }
        },
        {
          $project: {
            locationData: 0,
            physicalLocationData: 0
          }
        }
      ]

      const items = await getItemsAggregate({ collection, pipeline })
      if (!items) return
  
      // Search
      if (searchValue && searchValue !== searchQuery.value) return

      barometerSurveysCreated.value  = searchQuery.value ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
      totalBarometerSurveysCreated.value = barometerSurveysCreated.value.length
    
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.barometer_survey_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchBarometerSurveyCreated = searchValue => {
    isLoading.value = true
    fetchBarometerSurveysCreated(searchValue)
  }

  const deleteBarometerSurveyCreated = async (id) => {
    try {
      const query = { _id: id }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      fetchBarometerSurveysCreated()
      showSuccessMessage(i18n.t('message.barometer_survey_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (barometerSurveysCreated.value.length ? 1 : 0)
    const to = barometerSurveysCreated.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalBarometerSurveysCreated.value })
  })

  watch([currentPage, perPage], () => {
    fetchBarometerSurveysCreated()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchBarometerSurveysCreated()
  })

  const openSurvey = (link) => {
    window.open(link, "_blank")
  }

  const copyLink = (link) => {
    navigator.clipboard.writeText(link)
      .then(() => showSuccessMessage(i18n.t('message.linkCopiedToClipboard')))
      .catch(() => showErrorMessage(i18n.t('message.copyLinkError')))
  }

  const getEmailsWorkers = async () => {
    const query = {
      client_id: oid(clientId),
      deleted: { $ne: true },
      privileges: 'employee'
    }
    try {
      const items = await getItems({ collection: 'worker', query })
      if (items.length) {
        workers.value = items.map(({ _id, email, name, locations }) => ({
          _id,
          email,
          name,
          locations
        }))
      }
    } catch (error) {
      console.log(error)
    }
  }

  const confirmSendEmail = (data) => {
    if (!workers.value?.length || !data.link) return

    // Filter workers in the selected department who have an email
    const workersWithLocation = workers.value?.filter(worker => worker?.locations?.some(locId => locId?.toString() === data.locationId)) || []
    const workersWithEmail = workersWithLocation?.filter(w => !!w?.email) || []

    // Build the data for sending emails
    mailData.value = Object.assign(
      workersWithEmail?.map(({ name, email }) => ({ name: name || '', email: email || '' })) || [],
      {
        totalWithEmail: workersWithEmail?.length || 0,
        totalWithoutEmail: (workersWithLocation?.length || 0) - (workersWithEmail?.length || 0),
        locationName: data.location,
        surveyName: data.name || '',
        link: data.link || '',
      }
    )
    showMailModal.value = true
  }

  const handleSendMail = () => {
    if (!workers.value?.length || !mailData.value?.link) return

    mailData.value.forEach(worker => {
      if (!worker.email) return

      const subject = `${i18n.t('message.Survey')}: ${mailData.value.surveyName}`
      const body = getEmailBarometerSurveyTemplate({
        name: worker.name,
        surveyName: mailData.value.surveyName,
        locationName: mailData.value.locationName,
        link: mailData.value.link
      });

      sendEmail({ to: [worker.email], subject, htmlBody: body })
        .then((response) => {
          if (response.MessageId) console.log(`Correo enviado a ${worker.email}`)
          showSuccessMessage(i18n.t('message.survey_success'))
        })
        .catch((err) => {
          console.log(err);
          showErrorMessage(i18n.t('message.survey_submit_error'))
        })
    })
    showMailModal.value = false
  }

  return {
    fetchBarometerSurveysCreated,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalBarometerSurveysCreated,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    barometerSurveysCreated,
    deleteBarometerSurveyCreated,
    isLoading,
    searchBarometerSurveyCreated,
    openSurvey,
    copyLink,
    confirmSendEmail,
    showMailModal,
    handleSendMail,
    getLocationsForDropDown,
    mailData,
    getEmailsWorkers
  }
}